// uploadLimits — how big a file this app can actually send, and how an upload
// that is going nowhere gets to say so.
//
// The motivating bug (Alessandro, 2026-08-31): a 434 MB `Apartment.ply` dropped
// on the content browser sat at "0% — 363 KB / 434 MB" forever, under a modal
// reading "Large gsplat files can take a while — please don't close this tab."
// The file was never going to arrive. `nginx.conf.template`'s `/aam/` location
// capped bodies at 256m, so nginx answered 413 and stopped reading — and over
// HTTP/2 (h3 on staging) Chrome surfaces that to XHR as NOTHING AT ALL: one
// progress event at 371712 bytes, then `readyState 1`, no `load`, no `error`,
// no timeout, indefinitely. Measured on staging.avataros.xyz; the 363 KB in the
// screenshot is that same 371712 bytes.
//
// Two rules come out of that, and they are separate on purpose:
//
//   - A file over the limit is REFUSED BEFORE IT IS SENT (`oversizeRefusal`),
//     because the wire cannot report this one. The refusal names the file's
//     size and the ceiling, so the message is actionable ("re-encode as SPZ")
//     rather than "upload failed".
//   - An upload that stops progressing is FAILED (`stallVerdict`), because a
//     413 is only one way to get a silently wedged stream — a dropped
//     connection mid-body looks identical, and so would the next cap we add.
//     A stall detector cannot know why; it only has to refuse to pretend the
//     upload is still happening.
//
// MAX_UPLOAD_BYTES and NGINX_MAX_BODY are ONE decision written twice — the
// client's check and the proxy's cap must agree or the check is theatre. They
// cannot drift: `tests/unit/nginxRoutes.test.mjs` reads the value out of
// nginx.conf.template and asserts it equals NGINX_MAX_BODY.

/** The `client_max_body_size` on the asset-manager locations, verbatim. */
export const NGINX_MAX_BODY = '2g'

/**
 * The largest body the deployed proxy will pass, in bytes.
 *
 * 2 GB, not the old 256 MB. The cap was written for ENG-468's convorcher proxy,
 * before gsplat environments existed, and the environment catalogue outgrew it
 * without anyone noticing: `Startup_Level`'s own source PLY is 567,910,226
 * bytes (542 MB), so an environment already IN the catalogue could not be
 * re-uploaded through the app. Uploads that went straight to the asset-manager
 * (the MCP tools, tools/upload_*.mjs) never met the cap, which is why the hole
 * stayed open — the asset-manager itself answers `100 Continue` to a 434 MB
 * body.
 */
export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024

/**
 * No progress for this long means the upload is not happening. Generous on
 * purpose: a slow uplink still fires progress events as chunks leave, so this
 * catches a stream that has genuinely stopped, not a slow one.
 *
 * This applies ONLY while the body is still being sent. See RESPONSE_MS.
 */
export const STALL_MS = 60_000

/**
 * How long to wait for a response AFTER the whole body has been sent.
 *
 * A separate deadline, because the two waits are different events and the first
 * fix conflated them: once `loaded` reaches `total` the browser fires no further
 * upload-progress events — sending is over — so STALL_MS was measuring the
 * server's store time and calling it a stall. That aborted Apartment.ply
 * (455,283,057 bytes) at 100% after 61s, while the asset-manager was still
 * writing it. Shop.ply (392,857,723 bytes) stored in under 60s and survived,
 * which is why the regression looked size-dependent rather than universal.
 *
 * The value is calibrated against the proxy rather than chosen: the `/aam/`
 * location sets `proxy_read_timeout 300s`, so nginx itself gives up at five
 * minutes and returns 504, which the caller reports verbatim. A client deadline
 * inside that window would replace a precise "504" with a vaguer message of
 * ours, so this sits past it and is only the backstop for a response that never
 * comes from anywhere. Locked to the template by tests/unit/nginxRoutes.test.mjs.
 */
export const RESPONSE_MS = 360_000

function fmtBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = bytes, i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  // A whole number keeps no decimal: "2 GB" and "363 KB" are the numbers people
  // quote back at you, and "2.0 GB" reads like a measurement rather than a rule.
  const decimals = Number.isInteger(n) || n >= 10 || i === 0 ? 0 : 1
  return `${n.toFixed(decimals)} ${units[i]}`
}

/** The upload ceiling as a human string, for a message or a hint. */
export function describeLimit() {
  return fmtBytes(MAX_UPLOAD_BYTES)
}

/**
 * Why `bytes` cannot be uploaded, or null when it can.
 *
 * A string, not a boolean: the caller shows it, and the reason has to name both
 * numbers to be worth reading. `null`/unknown sizes pass — a body whose length
 * we cannot measure is not evidence that it is too big.
 */
export function oversizeRefusal(bytes) {
  if (!Number.isFinite(bytes) || bytes <= MAX_UPLOAD_BYTES) return null
  return (
    `This file is ${fmtBytes(bytes)}, over the ${describeLimit()} upload limit — ` +
    `the server rejects it before any of it arrives. Re-encode it smaller ` +
    `(SPZ is ~13x smaller than PLY for the same splat) and upload that.`
  )
}

/**
 * Why an in-flight upload should be failed, or null while it is healthy.
 *
 * `lastProgressAt` is when a progress event last moved `loaded`; `now`,
 * `stallMs` and `responseMs` are injected so this stays pure and testable.
 * Reports how far it got, because "stalled at 363 KB of 434 MB" is the sentence
 * that identifies this failure — a bare "upload failed" does not.
 *
 * TWO PHASES, and telling them apart is the whole point. While `loaded < total`
 * the body is still going out, and silence means the server stopped reading it.
 * Once `loaded` reaches `total` the send is COMPLETE and the browser stops
 * firing progress events — so the same silence means the opposite thing: the
 * file arrived and the answer is late. Judging both by `stallMs` is what failed
 * a healthy 455 MB upload at 100%. The comparison is on bytes, not on the
 * rendered size: fmtBytes prints "434 MB of 434 MB" well before loaded === total.
 */
export function stallVerdict({
  lastProgressAt, now, loaded = 0, total = 0,
  stallMs = STALL_MS, responseMs = RESPONSE_MS,
} = {}) {
  if (!Number.isFinite(lastProgressAt) || !Number.isFinite(now)) return null
  const idle = now - lastProgressAt
  const secs = Math.round(idle / 1000)

  // Post-body: the server has every byte. Never say the upload did not arrive.
  if (total > 0 && loaded >= total) {
    if (idle < responseMs) return null
    return (
      `The whole file (${fmtBytes(total)}) was uploaded, but the server has not ` +
      `answered for ${secs}s. It may still have been stored — reload the asset ` +
      `list before trying again, and check with the team if it is not there.`
    )
  }

  if (idle < stallMs) return null
  const got = total > 0 ? `${fmtBytes(loaded)} of ${fmtBytes(total)}` : fmtBytes(loaded)
  return (
    `Upload stalled at ${got} — no data accepted for ${secs}s. ` +
    `The server stopped reading the body (a size limit or a dropped connection); ` +
    `it will not finish on its own.`
  )
}
